import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { ProjectSample } from '../../@types/samples/types';
// Availability/selection is driven by a real computed result per strategy,
// not `performed_analyses` (which the backend flags even for skipped runs).
import { hasResultForStrategy } from '../../pages/MultiAnalysisPage/analysisCategory';
import { useAppContext } from '../AppContext';
import { useSelected } from '../selectedContext';
import {
  CalculationStrategies,
  CorrelationComparisonMode,
  CorrelationEndpointSource,
  FziFunctions,
  Project,
  XAxisVariable,
} from '../../@types/projects/types';
import { WorkerMessage, WorkerResponse } from './correlation.worker';
import { message } from 'antd';
import {
  calculateCorrelation,
  listAllProjectSamples,
  updateCorrelationPreferences
} from '../../api/data';
import {
  DEFAULT_ENDPOINT_COMPARISON_MODE,
  DEFAULT_ENDPOINT_SOURCE
} from '../../design-system/correlation/utils/endpointSourceConstants';

export type RefinementBlockFamily = 'water-oil' | 'gas-oil';

export interface RefinementBlockSample {
  family: RefinementBlockFamily;
  sampleCode: string;
  sampleUid: string;
  wellUid: string;
}

export interface RefinementBlockSummary {
  count: number;
  sampleCodes: string[];
  samples: RefinementBlockSample[];
  preview: string[];
  moreCount: number;
  families: RefinementBlockFamily[];
  message: string;
}

interface CorrelationContextProps {
  /**
   * Initially, just like the selectedInfo.project, but when the
   * correlation is calculated in worker, this is the object that changes.
   */
  localProject: Project | undefined;

  /**
   * The local instance of project samples.
   * This is the list that should change when the user
   * select or remove a sample from correlation.
   */
  projectSamples: ProjectSample[];

  /**
   * A flag created to indicate when the data that is
   * being shown is not saved in database.
   */
  isLocalCalculated: boolean;

  /**
   * The filtered samples that have gas-oil in performed_analyses
   */
  selectedGasOilSamples: ProjectSample[];

  /**
   * The filtered samples that have lambda in performed_analyses
   */
  selectedLambdaSamples: ProjectSample[];

  /**
   * The filtered samples that have liao in performed_analyses
   */
  selectedLiaoSamples: ProjectSample[];

  /**
   * The filtered samples that have water-oil in performed_analyses
   */
  selectedWaterOilSamples: ProjectSample[];

  /**
   * The filtered samples that are checked for gas-oil correlation
   */
  checkedGasOilSamples: ProjectSample[];

  /**
   * The filtered samples that are checked for lambda correlation
   */
  checkedLambdaSamples: ProjectSample[];

  /**
   * The filtered samples that are checked for liao correlation
   */
  checkedLiaoSamples: ProjectSample[];

  /**
   * The filtered samples that are checked for water-oil correlation
   */
  checkedWaterOilSamples: ProjectSample[];


  /**
   * The selected X-Axis variable, either 'fzi' or 'rqi'.
   * This is used to determine which variable to show in the charts.
   */
  selectedXAxis: XAxisVariable;

  /** Correlation functions for the currently selected X-axis, falling back to legacy FZI functions. */
  activeFziFunctions: FziFunctions | null;

  /** Correlation endpoint source selection for endpoint-based charts. */
  endpointSource: CorrelationEndpointSource;

  /** Comparison mode between endpoint sources. */
  comparisonMode: CorrelationComparisonMode;

  /** Endpoint source currently persisted in backend and used for correlation calculations. */
  appliedEndpointSource: CorrelationEndpointSource;

  /** Draft endpoint source to be persisted when user clicks Apply. */
  preferenceEndpointSource: CorrelationEndpointSource;

  /** True while applying endpoint source preference to backend. */
  isApplyingEndpointPreference: boolean;

  /** Whether every selected endpoint-correlation sample has the required saved refinement values. */
  canUseRefinementEndpointSource: boolean;

  /** Shared validation summary for persisted correlation source selection. */
  refinementPreferenceBlock: RefinementBlockSummary | null;

  /** Validation summary for Water-Oil local refinement view in correlation. */
  waterOilRefinementBlock: RefinementBlockSummary | null;

  /** Validation summary for Gas-Oil local refinement view in correlation. */
  gasOilRefinementBlock: RefinementBlockSummary | null;

  /** Just make the localProject and projectSamples equal to the original (saved) data. */
  resetProjectCorrelation: () => void;

  /** Replace the local correlation snapshot with a freshly persisted project. */
  syncProjectFromServer: (project: Project) => void;

  /** If the edit is canceled, the state is just reseted to the original */
  onCancelEditCorrelationSamples: () => Promise<void>;

  /** If the edit is confirmed, the request to recalculate the correlations is made */
  onConfirmEditCorrelationSamples: () => Promise<void>;

  /** Alias for onConfirmEditCorrelationSamples — saves correlation sample selection */
  handleSaveCorrelationSamples: () => Promise<void>;

  /** Set the local project samples and calculate the correlation in worker */
  handleSampleToggle: (
    sampleId: string,
    strategy: CalculationStrategies
  ) => Promise<void>;

  /** Set the checked state for all samples from a specific strategy in local projectSamples. */
  handleSelectAllStrategySamples: (
    strategy: CalculationStrategies,
    checked: boolean
  ) => Promise<void>;

  /** Set the local projectSamples when the point is clicked in the chart */
  handlePointClick: (
    sampleCode: string,
    analysisType: CalculationStrategies
  ) => void;

  toggleXAxisVariable: (value: XAxisVariable) => void;
  setEndpointSource: (value: CorrelationEndpointSource) => void;
  setComparisonMode: (value: CorrelationComparisonMode) => void;
  setPreferenceEndpointSource: (value: CorrelationEndpointSource) => void;
  applyEndpointSourcePreference: () => Promise<void>;
}

const strategyMap = {
  selectedLiaoSamples: CalculationStrategies.LIAO,
  selectedLambdaSamples: CalculationStrategies.LAMBDA,
  selectedGasOilSamples: CalculationStrategies.GAS_OIL_RELATIVE_PERMEABIILITY,
  selectedWaterOilSamples:
    CalculationStrategies.WATER_OIL_RELATIVE_PERMEABIILITY
} as const;

const CorrelationContext = createContext<CorrelationContextProps | undefined>(
  undefined
);

const hasNumber = (value: number | null | undefined): value is number =>
  typeof value === 'number';

const getFziFunctionsForAxis = (
  project: Project | undefined,
  xAxis: XAxisVariable
): FziFunctions | null => {
  const byAxis = project?.correlation?.fzi_functions_by_x_axis;
  return byAxis?.[xAxis] ?? (xAxis === 'fzi' ? project?.correlation?.fzi_functions ?? null : null);
};

const getFamilyLabel = (families: RefinementBlockFamily[]): string => {
  if (families.length === 2) {
    return 'Water-Oil and Gas-Oil';
  }

  return families[0] === 'water-oil' ? 'Water-Oil' : 'Gas-Oil';
};

const buildRefinementBlockSummary = (
  samples: ProjectSample[],
  family: RefinementBlockFamily
): RefinementBlockSummary | null => {
  if (samples.length === 0) {
    return null;
  }

  const mappedSamples = samples
    .filter(sample => Boolean(sample.sample_code))
    .map<RefinementBlockSample>(sample => ({
      family,
      sampleCode: sample.sample_code,
      sampleUid: sample.uid,
      wellUid: sample.well_uid
    }));

  const sampleCodes = Array.from(
    new Set(mappedSamples.map(sample => sample.sampleCode))
  );

  if (sampleCodes.length === 0) {
    return null;
  }

  const preview = sampleCodes.slice(0, 3);
  const moreCount = sampleCodes.length - preview.length;

  return {
    count: sampleCodes.length,
    sampleCodes,
    samples: mappedSamples,
    preview,
    moreCount,
    families: [family],
    message: `Refinement values unavailable for ${getFamilyLabel([family])}: ${sampleCodes.length} selected sample${
      sampleCodes.length === 1 ? '' : 's'
    } ${sampleCodes.length === 1 ? 'is' : 'are'} missing saved refinement values (click for more)`
  };
};

export const CorrelationProvider = ({ children }: React.PropsWithChildren) => {
  const { selectedInfo, setSelectedInfo } = useSelected();
  const { data, updateData, updateConfigStates } = useAppContext();

  const workerRef = useRef<Worker>();
  // Monotonic id stamped on each worker request so responses can be matched to
  // their request (and stale ones dropped). Mirrors src/components/results/results.tsx.
  const requestIdRef = useRef(0);
  // Generation of the latest correlation recalculation; a result is only applied
  // if it is still the latest, so rapid sample toggles don't churn localProject.
  const latestCorrelationRef = useRef(0);

  const [projectSamples, setProjectSamples] = useState<ProjectSample[]>([]);
  const [isLocalCalculated, setIsLocalCalculated] = useState(false);
  const [localProject, setLocalProject] = useState<Project | undefined>(undefined);

  const [selectedXAxis, setSelectedXAxis] = useState<XAxisVariable>('fzi');
  const [endpointSource, setEndpointSource] =
    useState<CorrelationEndpointSource>(DEFAULT_ENDPOINT_SOURCE);
  const [comparisonMode, setComparisonMode] = useState<CorrelationComparisonMode>(
    DEFAULT_ENDPOINT_COMPARISON_MODE
  );
  const [appliedEndpointSource, setAppliedEndpointSource] =
    useState<CorrelationEndpointSource>(DEFAULT_ENDPOINT_SOURCE);
  const [appliedComparisonMode, setAppliedComparisonMode] =
    useState<CorrelationComparisonMode>(DEFAULT_ENDPOINT_COMPARISON_MODE);
  const [preferenceEndpointSource, setPreferenceEndpointSource] =
    useState<CorrelationEndpointSource>(DEFAULT_ENDPOINT_SOURCE);
  const [isApplyingEndpointPreference, setIsApplyingEndpointPreference] =
    useState(false);

  const {
    selectedGasOilSamples,
    selectedLambdaSamples,
    selectedLiaoSamples,
    selectedWaterOilSamples
  } = useMemo<{ [key in keyof typeof strategyMap]: ProjectSample[] }>(() => {
    const strategies = Object.entries(strategyMap);

    return strategies.reduce(
      (acc, [key, strategy]) => {
        acc[key] = projectSamples.filter(sample =>
          hasResultForStrategy(sample, strategy)
        );

        return acc;
      },
      {} as Record<keyof typeof strategyMap, ProjectSample[]>
    );
  }, [projectSamples]);

  const checkedWaterOilSamples = useMemo(
    () =>
      projectSamples.filter(
        sample =>
          hasResultForStrategy(
            sample,
            CalculationStrategies.WATER_OIL_RELATIVE_PERMEABIILITY
          ) &&
          sample.selectedAnalyses?.[
            CalculationStrategies.WATER_OIL_RELATIVE_PERMEABIILITY
          ]
      ),
    [projectSamples]
  );

  const checkedGasOilSamples = useMemo(
    () =>
      projectSamples.filter(
        sample =>
          hasResultForStrategy(
            sample,
            CalculationStrategies.GAS_OIL_RELATIVE_PERMEABIILITY
          ) &&
          sample.selectedAnalyses?.[
            CalculationStrategies.GAS_OIL_RELATIVE_PERMEABIILITY
          ]
      ),
    [projectSamples]
  );

  const checkedLiaoSamples = useMemo(
    () =>
      projectSamples.filter(
        sample =>
          hasResultForStrategy(sample, CalculationStrategies.LIAO) &&
          sample.selectedAnalyses?.[CalculationStrategies.LIAO]
      ),
    [projectSamples]
  );

  const checkedLambdaSamples = useMemo(
    () =>
      projectSamples.filter(
        sample =>
          hasResultForStrategy(sample, CalculationStrategies.LAMBDA) &&
          sample.selectedAnalyses?.[CalculationStrategies.LAMBDA]
      ),
    [projectSamples]
  );

  const missingWaterOilRefinementSamples = useMemo(
    () =>
      checkedWaterOilSamples.filter(sample => {
        const values = sample.k_rel_values?.water_oil;
        const inputValues = sample.input_sample_values;
        return !(
          hasNumber(values?.true_sor_w ?? values?.sor_w) &&
          hasNumber(values?.true_krw ?? values?.krw_exponent ?? inputValues?.krw_absolute) &&
          hasNumber(values?.true_no_w ?? values?.no_w) &&
          hasNumber(values?.true_nw ?? values?.nw)
        );
      }),
    [checkedWaterOilSamples]
  );

  const missingGasOilRefinementSamples = useMemo(
    () =>
      checkedGasOilSamples.filter(sample => {
        const values = sample.k_rel_values?.gas_oil;
        const inputValues = sample.input_sample_values;
        return !(
          hasNumber(values?.true_sor_g ?? values?.sor_g) &&
          hasNumber(values?.true_krg ?? values?.krg_exponent ?? inputValues?.krg_absolute) &&
          hasNumber(values?.true_no_g ?? values?.no_g) &&
          hasNumber(values?.true_ng ?? values?.ng)
        );
      }),
    [checkedGasOilSamples]
  );

  const waterOilRefinementBlock = useMemo(
    () =>
      buildRefinementBlockSummary(
        missingWaterOilRefinementSamples,
        'water-oil'
      ),
    [missingWaterOilRefinementSamples]
  );

  const gasOilRefinementBlock = useMemo(
    () =>
      buildRefinementBlockSummary(missingGasOilRefinementSamples, 'gas-oil'),
    [missingGasOilRefinementSamples]
  );

  const refinementPreferenceBlock = useMemo<RefinementBlockSummary | null>(() => {
    const sampleMap = new Map<string, RefinementBlockSample>();

    if (waterOilRefinementBlock) {
      waterOilRefinementBlock.samples.forEach(sample => {
        sampleMap.set(sample.sampleUid, sample);
      });
    }

    if (gasOilRefinementBlock) {
      gasOilRefinementBlock.samples.forEach(sample => {
        sampleMap.set(sample.sampleUid, sample);
      });
    }

    const samples = Array.from(sampleMap.values());
    const sampleCodes = Array.from(new Set(samples.map(sample => sample.sampleCode)));

    if (sampleCodes.length === 0) {
      return null;
    }

    const families: RefinementBlockFamily[] = [];
    if (waterOilRefinementBlock) {
      families.push('water-oil');
    }
    if (gasOilRefinementBlock) {
      families.push('gas-oil');
    }

    const preview = sampleCodes.slice(0, 3);
    const moreCount = sampleCodes.length - preview.length;

    return {
      count: sampleCodes.length,
      sampleCodes,
      samples,
      preview,
      moreCount,
      families,
      message: `Refinement values unavailable for ${getFamilyLabel(families)}: ${sampleCodes.length} selected sample${
        sampleCodes.length === 1 ? '' : 's'
      } ${sampleCodes.length === 1 ? 'is' : 'are'} missing saved refinement values (click for more)`
    };
  }, [gasOilRefinementBlock, waterOilRefinementBlock]);

  const canUseRefinementEndpointSource = refinementPreferenceBlock == null;

  const activeFziFunctions = useMemo(
    () => getFziFunctionsForAxis(localProject, selectedXAxis),
    [localProject, selectedXAxis]
  );

  const originalSelectedProject = useMemo(
    () =>
      (data.projects ?? []).find(
        project => project.uid === selectedInfo.project?.uid
      ),
    [selectedInfo.project?.uid, data.projects]
  );

  /** Just make the localProject and projectSamples equal to the original (saved) data. */
  const resetProjectCorrelation = useCallback(() => {
    setLocalProject(originalSelectedProject);
    setProjectSamples(data.projectSamples);
    setIsLocalCalculated(false);
  }, [originalSelectedProject, data.projectSamples]);

  const syncProjectFromServer = useCallback((project: Project) => {
    setLocalProject(project);
    setIsLocalCalculated(false);
  }, []);

  useEffect(() => {
    if (data.projectSamples.length === 0) return;

    setProjectSamples(currentSamples => {
      if (currentSamples.length === 0) return data.projectSamples;

      const currentByUid = new Map(
        currentSamples.map(sample => [sample.uid, sample])
      );
      let changed = false;
      const mergedSamples = data.projectSamples.map(sharedSample => {
        const currentSample = currentByUid.get(sharedSample.uid);
        if (!currentSample) {
          changed = true;
          return sharedSample;
        }

        if (currentSample === sharedSample) return currentSample;

        changed = true;
        return {
          ...sharedSample,
          selectedAnalyses: currentSample.selectedAnalyses,
          selected: currentSample.selected
        };
      });

      if (currentSamples.length !== mergedSamples.length) {
        changed = true;
      }

      return changed ? mergedSamples : currentSamples;
    });
  }, [data.projectSamples]);

  const calculateInWorker = useCallback(
    async (input: WorkerMessage): Promise<WorkerResponse> => {
      const worker = workerRef.current;
      if (!worker) {
        return Promise.reject(new Error('Worker not initialized'));
      }

      const requestId = ++requestIdRef.current;

      return new Promise((resolve, reject) => {
        const handleMessage = (
          e: MessageEvent<
            | { project: WorkerResponse; requestId?: number }
            | { __error__: true; message: string; requestId?: number }
          >
        ) => {
          const data = e.data as any;
          // Ignore responses for superseded requests; let the matching one arrive.
          if (data?.requestId !== requestId) return;
          if (data?.__error__) {
            reject(new Error(data.message ?? 'Unexpected error in correlation worker'));
          } else {
            resolve(data.project as WorkerResponse);
          }
          cleanup();
        };

        const handleError = (e: ErrorEvent) => {
          reject(new Error(e.message));
          cleanup();
        };

        const handleMessageError = (e: MessageEvent) => {
          reject(new Error('Failed to deserialize message from worker'));
          cleanup();
        };

        const cleanup = () => {
          worker.removeEventListener('message', handleMessage);
          worker.removeEventListener('error', handleError);
          worker.removeEventListener('messageerror', handleMessageError);
        };

        worker.addEventListener('message', handleMessage);
        worker.addEventListener('error', handleError);
        worker.addEventListener('messageerror', handleMessageError);

        worker.postMessage({ ...input, requestId });
      });
    },
    []
  );

  const calculateCorrelationInWorker = useCallback(
    async (newProjectSamples: ProjectSample[]) => {
      if (!localProject) return;
      const generation = ++latestCorrelationRef.current;
      const payload = {
        project_uid: localProject.uid,
        liao_samples_uid: newProjectSamples
          .filter(s => s.selectedAnalyses?.[CalculationStrategies.LIAO])
          .map(s => s.uid),
        lambda_samples_uid: newProjectSamples
          .filter(s => s.selectedAnalyses?.[CalculationStrategies.LAMBDA])
          .map(s => s.uid),
        water_oil_samples_uid: newProjectSamples
          .filter(
            s =>
              s.selectedAnalyses?.[
              CalculationStrategies.WATER_OIL_RELATIVE_PERMEABIILITY
              ]
          )
          .map(s => s.uid),
        gas_oil_samples_uid: newProjectSamples
          .filter(
            s =>
              s.selectedAnalyses?.[
              CalculationStrategies.GAS_OIL_RELATIVE_PERMEABIILITY
              ]
          )
          .map(s => s.uid),
        endpoint_source: appliedEndpointSource,
        comparison_mode: appliedComparisonMode
      };

      try {
        const result = await calculateInWorker({
          payload,
          project: localProject,
          // Use the freshest working copy (single source of truth) rather than the
          // mirrored AppContext array.
          samples: newProjectSamples
        });
        // Drop the result if a newer recalculation has since started.
        if (generation !== latestCorrelationRef.current) return;
        setLocalProject(result);

        return setIsLocalCalculated(true);
      } catch (error) {
        if (error instanceof Error) {
          message.error(error.message);
          return;
        }
        message.error('Error trying to calculate liao');
        return;
      }
    },
    [
      localProject,
      calculateInWorker,
      appliedEndpointSource,
      appliedComparisonMode
    ]
  );

  /** If the edit is canceled, the state is just reseted to the original */
  const onCancelEditCorrelationSamples = useCallback(async () => {
    if (!data.projectSamples) return;
    updateConfigStates({ editCorrelationSamples: false });
    setProjectSamples(data.projectSamples);
    await calculateCorrelationInWorker(data.projectSamples);
  }, [data.projectSamples, updateConfigStates, calculateCorrelationInWorker]);

  /** If the edit is confirmed, the request to recalculate the correlations is made */
  const onConfirmEditCorrelationSamples = useCallback(async () => {
    if (!selectedInfo.project) return;
    try {
      updateConfigStates({ loadingProjectSamples: true });

      const payload = {
        project_uid: selectedInfo.project.uid,
        liao_samples_uid: projectSamples
          .filter(s => s.selectedAnalyses?.[CalculationStrategies.LIAO])
          .map(s => s.uid),
        lambda_samples_uid: projectSamples
          .filter(s => s.selectedAnalyses?.[CalculationStrategies.LAMBDA])
          .map(s => s.uid),
        water_oil_samples_uid: projectSamples
          .filter(
            s =>
              s.selectedAnalyses?.[
              CalculationStrategies.WATER_OIL_RELATIVE_PERMEABIILITY
              ]
          )
          .map(s => s.uid),
        gas_oil_samples_uid: projectSamples
          .filter(
            s =>
              s.selectedAnalyses?.[
              CalculationStrategies.GAS_OIL_RELATIVE_PERMEABIILITY
              ]
          )
          .map(s => s.uid),
        endpoint_source: appliedEndpointSource,
        comparison_mode: appliedComparisonMode
      };

      const response = await calculateCorrelation(payload);

      setSelectedInfo({ project: response });

      // Update the listed projects, so if the user change between them,
      // the data will still be updated.
      const newProjects = data.projects.map(project => {
        if (project.uid === response.uid) {
          return response;
        }

        return project;
      });
      updateData({ projects: newProjects, projectSamples });
      setProjectSamples(projectSamples);
    } catch (error) {
      console.error(error);
      // If any error happens, should we reset the projectSamples?
      // setProjectSamples(data.projectSamples);
    } finally {
      updateConfigStates({
        editCorrelationSamples: false,
        loadingProjectSamples: false
      });
    }
  }, [
    data.projects,
    appliedEndpointSource,
    appliedComparisonMode,
    projectSamples,
    selectedInfo.project,
    setSelectedInfo,
    updateData,
    updateConfigStates
  ]);

  /** Set the local project samples and calculate the correlation in worker */
  const handleSampleToggle = useCallback(
    async (sampleId: string, strategy: CalculationStrategies) => {
      setProjectSamples(old => {
        const updatedProjectSamples = old.map(sample => {
          if (sample.uid === sampleId) {
            return {
              ...sample,
              selectedAnalyses: {
                ...sample.selectedAnalyses,
                [strategy]: !sample.selectedAnalyses[strategy]
              }
            };
          }
          return sample;
        });

        calculateCorrelationInWorker(updatedProjectSamples);
        return updatedProjectSamples;
      });
      updateConfigStates({ editCorrelationSamples: true });
    },
    [calculateCorrelationInWorker, updateConfigStates]
  );

  /** Set the checked state for all samples from a specific strategy in local projectSamples. */
  const handleSelectAllStrategySamples = useCallback(
    async (strategy: CalculationStrategies, checked: boolean) => {
      setProjectSamples(old => {
        const updatedProjectSamples = old.map(sample => {
          // Only (de)select samples that can actually contribute a fit —
          // matches what the picker displays.
          if (!hasResultForStrategy(sample, strategy)) return sample;
          return {
            ...sample,
            selectedAnalyses: {
              ...sample.selectedAnalyses,
              [strategy]: checked
            }
          };
        });

        calculateCorrelationInWorker(updatedProjectSamples);

        return updatedProjectSamples;
      });
      updateConfigStates({ editCorrelationSamples: true });
    },
    [calculateCorrelationInWorker, updateConfigStates]
  );

  /** Set the local project samples when the point is clicked in the chart */
  const handlePointClick = useCallback(
    async (sampleCode: string, analysisType: CalculationStrategies) => {
      setProjectSamples(old => {
        const updatedProjectSamples = old.map(sample => {
          if (sample.sample_code !== sampleCode) return sample;

          return {
            ...sample,
            selectedAnalyses: {
              ...sample.selectedAnalyses,
              [analysisType]: !sample.selectedAnalyses?.[analysisType]
            }
          };
        });

        calculateCorrelationInWorker(updatedProjectSamples);
        return updatedProjectSamples;
      });
      updateConfigStates({ editCorrelationSamples: true });
    },
    [calculateCorrelationInWorker, updateConfigStates]
  );

  // useEffect to load all project samples and set the localProject when the selected project changes
  useEffect(() => {
    const loadSamples = async () => {
      if (!selectedInfo.project?.uid) return;
      updateConfigStates({ loadingProjectSamples: true });

      try {
        const response = await listAllProjectSamples(selectedInfo.project.uid);

        const initializedSamples = response.map(sample => {
          const correlationSamplesByType =
            selectedInfo.project?.correlation?.samples_uid_by_type;

          const selectedAnalyses = Object.values(CalculationStrategies).reduce(
            (acc, strategy) => {
              const performedAnalyses = sample.performed_analyses || [];
              const haveTheAnalyse = performedAnalyses.includes(strategy);
              const correlationSampleUids =
                correlationSamplesByType?.[strategy] ?? [];
              const isSampleIncludedInCorrelation =
                correlationSampleUids.includes(sample.uid);

              acc[strategy] = haveTheAnalyse && isSampleIncludedInCorrelation;

              return acc;
            },
            {} as Record<CalculationStrategies, boolean>
          );

          return {
            ...sample,
            performed_analyses: sample.performed_analyses || [],
            selectedAnalyses
          };
        }) as ProjectSample[];

        // Single source of truth: store ONE array reference. `projectSamples`
        // (provider) is the editable working copy used by correlation/zones and
        // the worker; AppContext keeps the canonical list consumed elsewhere
        // (e.g. sample deletion in confirmModal, resets). Both reference the same
        // array here and share leaf curve arrays by reference, so this is not a
        // deep copy — do not "fix" it by cloning.
        setProjectSamples(initializedSamples);
        updateData({ projectSamples: initializedSamples });

        const project = selectedInfo.project;
        if (project) {
          const endpointSrc =
            project.correlation?.preferences?.endpoint_source ??
            DEFAULT_ENDPOINT_SOURCE;
          const compMode =
            project.correlation?.preferences?.comparison_mode ??
            DEFAULT_ENDPOINT_COMPARISON_MODE;

          const payload = {
            project_uid: project.uid,
            liao_samples_uid: initializedSamples
              .filter(s => s.selectedAnalyses?.[CalculationStrategies.LIAO])
              .map(s => s.uid),
            lambda_samples_uid: initializedSamples
              .filter(s => s.selectedAnalyses?.[CalculationStrategies.LAMBDA])
              .map(s => s.uid),
            water_oil_samples_uid: initializedSamples
              .filter(
                s =>
                  s.selectedAnalyses?.[
                    CalculationStrategies.WATER_OIL_RELATIVE_PERMEABIILITY
                  ]
              )
              .map(s => s.uid),
            gas_oil_samples_uid: initializedSamples
              .filter(
                s =>
                  s.selectedAnalyses?.[
                    CalculationStrategies.GAS_OIL_RELATIVE_PERMEABIILITY
                  ]
              )
              .map(s => s.uid),
            endpoint_source: endpointSrc,
            comparison_mode: compMode
          };

          const result = await calculateInWorker({
            payload,
            project,
            samples: initializedSamples
          });
          setLocalProject(result);
        }
      } catch (error) {
        console.error('Error loading samples:', error);
      } finally {
        updateConfigStates({ loadingProjectSamples: false });
      }
    };

    loadSamples();
    setLocalProject(selectedInfo.project);
    setEndpointSource(
      selectedInfo.project?.correlation?.preferences?.endpoint_source ??
      DEFAULT_ENDPOINT_SOURCE
    );
    setComparisonMode(
      selectedInfo.project?.correlation?.preferences?.comparison_mode ??
      DEFAULT_ENDPOINT_COMPARISON_MODE
    );
    setAppliedEndpointSource(
      selectedInfo.project?.correlation?.preferences?.endpoint_source ??
      DEFAULT_ENDPOINT_SOURCE
    );
    setAppliedComparisonMode(
      selectedInfo.project?.correlation?.preferences?.comparison_mode ??
      DEFAULT_ENDPOINT_COMPARISON_MODE
    );
    setPreferenceEndpointSource(
      selectedInfo.project?.correlation?.preferences?.endpoint_source ??
      DEFAULT_ENDPOINT_SOURCE
    );
    setSelectedXAxis(
      selectedInfo.project?.correlation?.preferences?.x_variable ?? 'fzi'
    );
    // Disable the eslint here because we only want the request to be made when the project uid change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInfo.project?.uid]);

  const toggleXAxisVariable = useCallback((value: XAxisVariable) => {
    setSelectedXAxis(value);
    if (selectedInfo.project?.uid) {
      updateCorrelationPreferences(selectedInfo.project.uid, {
        endpoint_source: appliedEndpointSource,
        comparison_mode: appliedComparisonMode,
        applies_to: 'both',
        x_variable: value,
      }).catch(() => {});
    }
  }, [selectedInfo.project?.uid, appliedEndpointSource, appliedComparisonMode]);

  useEffect(() => {
    if (!localProject || projectSamples.length === 0) return;
    calculateCorrelationInWorker(projectSamples);
  }, [appliedEndpointSource, appliedComparisonMode]);

  useEffect(() => {
    const source =
      selectedInfo.project?.correlation?.preferences?.endpoint_source ??
      DEFAULT_ENDPOINT_SOURCE;
    const mode =
      selectedInfo.project?.correlation?.preferences?.comparison_mode ??
      DEFAULT_ENDPOINT_COMPARISON_MODE;

    setAppliedEndpointSource(source);
    setAppliedComparisonMode(mode);
    setPreferenceEndpointSource(source);
  }, [
    selectedInfo.project?.correlation?.preferences?.endpoint_source,
    selectedInfo.project?.correlation?.preferences?.comparison_mode
  ]);

  const applyEndpointSourcePreference = useCallback(async () => {
    if (!selectedInfo.project?.uid) return;
    if (preferenceEndpointSource === appliedEndpointSource) return;
    if (
      preferenceEndpointSource === 'refinement' &&
      !canUseRefinementEndpointSource
    ) {
      message.warning(
        refinementPreferenceBlock?.message ??
          'Refinement values require saved refinement endpoints for all selected samples.'
      );
      return;
    }

    try {
      setIsApplyingEndpointPreference(true);

      const response = await updateCorrelationPreferences(selectedInfo.project.uid, {
        endpoint_source: preferenceEndpointSource,
        comparison_mode: appliedComparisonMode,
        applies_to: 'both'
      });

      const responsePreferences = response.correlation?.preferences;
      const nextAppliedEndpointSource =
        responsePreferences?.endpoint_source ?? preferenceEndpointSource;
      const nextAppliedComparisonMode =
        responsePreferences?.comparison_mode ?? appliedComparisonMode;

      setAppliedEndpointSource(nextAppliedEndpointSource);
      setAppliedComparisonMode(nextAppliedComparisonMode);
      setPreferenceEndpointSource(nextAppliedEndpointSource);
      setSelectedInfo({ project: response });
      setLocalProject(prev => {
        if (!prev?.correlation) return prev;

        return {
          ...prev,
          correlation: {
            ...prev.correlation,
            preferences: responsePreferences ?? prev.correlation.preferences
          }
        };
      });
      message.success('Correlation endpoint source applied.');
    } catch (error) {
      console.warn('Failed to persist correlation preferences', error);
      message.warning('Could not persist endpoint source preference.');
    } finally {
      setIsApplyingEndpointPreference(false);
    }
  }, [
    selectedInfo.project?.uid,
    selectedInfo.project,
    preferenceEndpointSource,
    appliedEndpointSource,
    canUseRefinementEndpointSource,
    refinementPreferenceBlock,
    appliedComparisonMode,
    setSelectedInfo
  ]);

  // Sync zones into localProject when they change (e.g. after zone creation/deletion)
  // without resetting locally-calculated correlation data.
  useEffect(() => {
    const zones = selectedInfo.project?.correlation?.zones;
    if (!zones) return;
    setLocalProject(prev => {
      if (!prev?.correlation) return prev;
      return {
        ...prev,
        correlation: { ...prev.correlation, zones }
      };
    });
  }, [selectedInfo.project?.correlation?.zones]);

  // useEffect to set the worker ref
  useEffect(() => {
    workerRef.current = new Worker(
      new URL('./correlation.worker.ts', import.meta.url),
      {
        type: 'module'
      }
    );

    return () => {
      workerRef.current?.terminate();
    };
  }, []);

  return (
    <CorrelationContext.Provider
      value={{
        localProject,
        projectSamples,
        isLocalCalculated,
        selectedGasOilSamples,
        selectedLambdaSamples,
        selectedLiaoSamples,
        selectedWaterOilSamples,
        checkedGasOilSamples,
        checkedLambdaSamples,
        checkedLiaoSamples,
        checkedWaterOilSamples,
        selectedXAxis,
        activeFziFunctions,
        endpointSource,
        comparisonMode,
        appliedEndpointSource,
        preferenceEndpointSource,
        isApplyingEndpointPreference,
        canUseRefinementEndpointSource,
        refinementPreferenceBlock,
        waterOilRefinementBlock,
        gasOilRefinementBlock,
        handleSampleToggle,
        onCancelEditCorrelationSamples,
        onConfirmEditCorrelationSamples,
        handleSaveCorrelationSamples: onConfirmEditCorrelationSamples,
        resetProjectCorrelation,
        syncProjectFromServer,
        handleSelectAllStrategySamples,
        handlePointClick,
        toggleXAxisVariable,
        setEndpointSource,
        setComparisonMode,
        setPreferenceEndpointSource,
        applyEndpointSourcePreference
      }}
    >
      {children}
    </CorrelationContext.Provider>
  );
};

export const useCorrelation = (): CorrelationContextProps => {
  const context = useContext(CorrelationContext);
  if (!context) {
    throw new Error('useCorrelation must be used within a CorrelationProvider');
  }
  return context;
};
